import type { UserRole } from '@/app/dashboard/models/dashboard.model';

import permissionsConfig from './permissions.config.json';

/**
 * Static permission matrix imported from `permissions.config.json`.
 *
 * The matrix is bundled at build time so the runtime layer can resolve
 * `can$('vault.create')` synchronously without an extra HTTP request.
 * Backends remain the security boundary — this matrix only drives UI
 * affordances (hiding buttons, gating sidebar entries, redirecting
 * forbidden routes). Server endpoints continue to enforce authorisation
 * and will reject any request the FE incorrectly allows.
 *
 * If/when permissions need to be reloadable at runtime, swap this import
 * for an HttpClient.get('assets/permissions.json') without changing the
 * downstream API.
 */
export const PERMISSIONS_CONFIG: PermissionsConfig =
  permissionsConfig as PermissionsConfig;

/** Permission categories used for docs/devtools grouping. */
export type PermissionCategory =
  | 'navigation'
  | 'dashboard'
  | 'users'
  | 'vaults'
  | 'wallets'
  | 'whitelist'
  | 'transactions'
  | 'approvals'
  | 'policies'
  | 'security'
  | 'emergency'
  | 'reports'
  | 'account';

/**
 * Delivery semantics for notifications.
 *
 * - `broadcast` — every user holding one of the `allowedRoles` receives the notification.
 * - `related`   — the role gates access but the user must also be tied to the underlying
 *                 record (e.g. an owner/signer/approver of a particular withdrawal). The
 *                 second-level check happens server-side; the UI only filters on role.
 * - `personal`  — sent only to the individual user the event happened to (session,
 *                 password, device events). `allowedRoles` covers every role since any
 *                 role can trigger/receive these.
 */
export type NotificationScope = 'broadcast' | 'related' | 'personal';

export interface PermissionEntry {
  id: string;
  description: string;
  category: PermissionCategory;
  /**
   * Canonical route path used by `permissionGuard` to gate `nav.*` entries.
   * Only present on `category: 'navigation'`. Must match `app.routes.ts`
   * exactly — the guard does a literal compare against the resolved URL.
   */
  route?: string;
  allowedRoles: UserRole[];
  note?: string;
}

export interface NotificationPermissionEntry {
  id: string;
  allowedRoles: UserRole[];
  scope: NotificationScope;
  note?: string;
}

export interface PermissionsConfig {
  version: number;
  roles: UserRole[];
  permissions: PermissionEntry[];
  notifications: NotificationPermissionEntry[];
  _notes?: string[];
}

/**
 * String-literal union of every `permissions[].id` in the matrix. Authored
 * as a wide `string` here (rather than a generated literal union) so adding
 * a permission only requires editing the JSON. The runtime `PermissionService`
 * already returns `false` for unknown ids, so a typo at a call-site is a
 * silent UX bug (button hidden / route redirected) rather than a compile
 * error — pair `*hasPermission` usage with feature unit tests.
 */
export type PermissionId = string;

/** String-literal union of every `notifications[].id` in the matrix. */
export type NotificationId = string;
